import React from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";

const TAG_BY_DURATION = {
  90: "short_term_breakthrough",
  180: "heart_healing",
  365: "soul_covenant",
};

const CURRENCY_FORMAT = {
  en: (a) => `$${a.toFixed(0)} USD`,
  "zh-HK": (a) => `HK$${a.toFixed(0)}`,
  "zh-TW": (a) => `NT$${a.toFixed(0)}`,
  "zh-CN": (a) => `¥${a.toFixed(0)} RMB`,
};

export const PRESCRIPTION_PRICING = {
  en:    { 90: 19, 180: 22, 365: 25 },
  "zh-HK": { 90: 148, 180: 168, 365: 199 },
  "zh-TW": { 90: 590, 180: 690, 365: 790 },
  "zh-CN": { 90: 128, 180: 148, 365: 168 },
};

const formatDeliveryDate = (lang, duration) => {
  const d = new Date();
  d.setDate(d.getDate() + duration);
  if (lang.startsWith("zh")) {
    return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日`;
  }
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

export const PrescriptionSelector = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language in PRESCRIPTION_PRICING ? i18n.language : "en";
  const isZh = lang.startsWith("zh");
  const fmt = CURRENCY_FORMAT[lang];

  return (
    <section data-testid="prescription-selector">
      <div className="mb-7">
        <p className="text-[11px] uppercase tracking-[0.36em] text-[var(--wax)] mb-3">
          {t("prescription.section_kicker")}
        </p>
        <h3 className={`${isZh ? "font-cn" : "font-display"} text-2xl md:text-3xl tracking-tight`}>
          {t("prescription.section_title")}
        </h3>
        <p className="mt-2 text-[var(--ink-soft)] italic text-[15px]">
          {t("prescription.section_subtitle")}
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {[90, 180, 365].map((duration) => {
          const tag = TAG_BY_DURATION[duration];
          const opt = t(`prescription.options.${tag}`, { returnObjects: true });
          const price = PRESCRIPTION_PRICING[lang][duration];
          const selected = value === duration;
          const isClassic = duration === 365;
          return (
            <button
              type="button"
              key={duration}
              onClick={() => onChange(duration)}
              role="radio"
              aria-checked={selected}
              data-testid={`prescription-card-${duration}`}
              className={`group relative text-left p-6 md:p-7 border transition-all duration-300 bg-[var(--bg-paper)] flex flex-col ${
                selected
                  ? "border-[var(--wax)] shadow-[0_22px_44px_-22px_rgba(138,58,51,0.45)] -translate-y-0.5"
                  : "border-[var(--rule)] hover:border-[var(--ink-soft)]"
              }`}
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.11  0 0 0 0 0.1  0 0 0 0 0.09  0 0 0 0 0.05 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
              }}
            >
              {isClassic && (
                <span
                  data-testid="classic-badge"
                  className="absolute -top-2.5 right-4 bg-[var(--wax)] text-[var(--bg-paper)] text-[9px] tracking-[0.22em] uppercase px-2.5 py-1 inline-flex items-center gap-1"
                >
                  <Sparkles size={10} /> {t("prescription.classic_pick")}
                </span>
              )}

              <div
                className={`text-[10px] uppercase tracking-[0.3em] mb-3 ${
                  selected ? "text-[var(--wax)]" : "text-[var(--ink-soft)]"
                }`}
                data-testid={`tag-label-${duration}`}
              >
                [ {opt.tag_label} ]
              </div>

              <div
                className={`${isZh ? "font-cn" : "font-display"} text-[22px] md:text-[24px] text-[var(--ink)] tracking-tight leading-snug`}
                data-testid={`tag-title-${duration}`}
              >
                {opt.title}
              </div>

              <p className={`${isZh ? "font-cn" : "font-en"} text-[14px] md:text-[15px] text-[var(--ink-soft)] mt-3 leading-[1.7]`}>
                {opt.subtext}
              </p>

              <div className="mt-auto pt-6">
              <div className="pt-4 border-t border-dashed border-[var(--rule)] space-y-2">
                <div className="font-display text-[26px] text-[var(--ink)] leading-none" data-testid={`price-${duration}`}>
                  {fmt(price)}
                </div>
                <div className="text-[12px] text-[var(--ink-soft)] italic">
                  {t("prescription.delivery_label")}
                </div>
                <div className={`${isZh ? "font-cn" : "font-display"} text-[15px] text-[var(--ink)]`} data-testid={`delivery-${duration}`}>
                  {formatDeliveryDate(lang, duration)}
                </div>
              </div>
              </div>

              <div
                className={`absolute top-4 left-4 w-3 h-3 rounded-full border transition-colors ${
                  selected ? "bg-[var(--wax)] border-[var(--wax)]" : "border-[var(--ink-mute)]"
                }`}
                aria-hidden
              />
            </button>
          );
        })}
      </div>
    </section>
  );
};
